/**
 * @licence Copyright © 2019 Mercury Redstone BV, all rights reserved
 */
import { useState, useEffect } from 'react';
import { isEmpty, uniqBy, findKey } from 'lodash-es';
import { type ApolloClient } from '@apollo/client';
import { sendSentryError } from '../utils/sentry';
import {
  NotificationType,
  useUserNotificationsQuery,
  UserExchangesDocument,
  type UserNotificationsQueryHookResult,
} from '../apollo';
import { useAlertModal, type AlertModalContentType } from './useAlertModal';
import { useWindowTabIsActive } from './useWindowTabIsActive';
import { useModals, type Modal } from '../providers';

export const useUserNotifications = () => {
  const {
    openedModalName,
    modalOpened,
    dispatch: dispatchAlertModal,
  } = useAlertModal();
  const { dispatch: dispatchModal } = useModals<Modal>();
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const tabIsActive = useWindowTabIsActive();

  const { client, data, refetch, startPolling, stopPolling } =
    useUserNotificationsQuery({
      fetchPolicy: 'no-cache',
      pollInterval,
    });

  // Saving notifications to state
  useEffect(() => {
    if (data?.getUserNotifications) {
      setNotifications((prevNotifications) => {
        const mergedNotifications = uniqBy(
          [...prevNotifications, ...data.getUserNotifications],
          'type'
        );

        // Filtering out substitutable notifications
        return mergedNotifications.filter(({ type }, _, arr) => {
          const subTypes = notificationsData[type].substitutableNotifications;
          if (!subTypes) return true;
          return !arr.some((n) => subTypes.includes(n.type));
        });
      });
    }
  }, [data]);

  // Displaying notifications: normal or as substitute for an opened alert
  useEffect(() => {
    if (isEmpty(notifications)) return;

    let target: Notification | undefined;

    if (openedModalName) {
      const subTypes = getSubTypesByAlertName(openedModalName);
      target = notifications.find(({ type }) => subTypes?.includes(type));
    } else if (!modalOpened) {
      target = notifications[0];
    }

    if (!target) return;

    const config = notificationsData[target.type];

    const isSilentTransition =
      openedModalName &&
      silentTransitions[openedModalName]?.includes(target.type);

    if (isSilentTransition) {
      dispatchAlertModal(['closeModal']);
    } else {
      dispatchAlertModal(['setModalContent', config.alertName, undefined, true]);
    }

    if (config.dismissesModals) {
      dispatchModal({
        type: 'dismissModals',
        payload: { names: config.dismissesModals },
      });
    }

    config.extraAction?.({ client });

    setNotifications((prev) => prev.filter((n) => n !== target));
  }, [
    notifications,
    openedModalName,
    modalOpened,
    client,
    dispatchAlertModal,
    dispatchModal,
  ]);

  useEffect(() => {
    if (tabIsActive) {
      refetch();
      startPolling(pollInterval);
    } else {
      stopPolling();
    }
  }, [tabIsActive, refetch, startPolling, stopPolling]);
};

type Notification = NonNullable<
  UserNotificationsQueryHookResult['data']
>['getUserNotifications'][number];

const portfolioUpdateRelatedModals = [
  'newRebalance',
  'expectedPortfolio',
] satisfies Modal[];

const silentTransitions: Partial<
  Record<AlertModalContentType, NotificationType[]>
> = {
  initialPortfolioRebalanceStart: [NotificationType.PortfolioUpdateFinished],
};

/*
 * If there will be no another notifications,
 * consider to add extraAction: updateExchangesAndPortfolioFn
 * with one line above
 * */
const notificationsData: Readonly<{
  [key in NotificationType]: {
    alertName: AlertModalContentType;
    substitutableNotifications?: NotificationType[];
    dismissesModals?: Modal[];
    extraAction?: (data: { client: ApolloClient<any> }) => unknown;
  };
}> = {
  [NotificationType.PortfolioUpdateInProgress]: {
    alertName: 'rebalanceInProgress',
    substitutableNotifications: [
      NotificationType.PortfolioUpdateFinished,
      NotificationType.PortfolioUpdateFailed,
      NotificationType.UserExchangeLockedKraken,
      NotificationType.UserExchangeLockedBitvavo,
    ],
    dismissesModals: portfolioUpdateRelatedModals,
    extraAction: updateExchangesAndPortfolioFn,
  },
  [NotificationType.PortfolioUpdateFinished]: {
    alertName: 'rebalanceFinalised',
    dismissesModals: portfolioUpdateRelatedModals,
    extraAction: updateExchangesAndPortfolioFn,
  },
  [NotificationType.PortfolioUpdateFailed]: {
    alertName: 'rebalanceFailed',
    substitutableNotifications: [
      NotificationType.PortfolioUpdateFinished,
      NotificationType.UserExchangeLockedKraken,
      NotificationType.UserExchangeLockedBitvavo,
    ],
    dismissesModals: portfolioUpdateRelatedModals,
    extraAction: updateExchangesAndPortfolioFn,
  },
  [NotificationType.BuySellInProgress]: {
    alertName: 'buySellOrderInProgress',
    substitutableNotifications: [
      NotificationType.BuySellFinished,
      NotificationType.BuySellFailed,
      NotificationType.UserExchangeLockedKraken,
      NotificationType.UserExchangeLockedBitvavo,
    ],
    extraAction: updateExchangesAndPortfolioFn,
  },
  [NotificationType.BuySellFinished]: {
    alertName: 'buySellOrderFinalised',
    extraAction: updateExchangesAndPortfolioFn,
  },
  [NotificationType.BuySellFailed]: {
    alertName: 'buySellOrderFailed',
    substitutableNotifications: [
      NotificationType.BuySellFinished,
      NotificationType.UserExchangeLockedKraken,
      NotificationType.UserExchangeLockedBitvavo,
    ],
    extraAction: updateExchangesAndPortfolioFn,
  },
  [NotificationType.UserExchangeLockedKraken]: {
    alertName: 'krakenExchangeLocked',
    substitutableNotifications: [
      NotificationType.PortfolioUpdateFinished,
      NotificationType.BuySellFinished,
    ],
    dismissesModals: portfolioUpdateRelatedModals,
    extraAction: updateExchangesAndPortfolioFn,
  },
  [NotificationType.UserExchangeLockedBitvavo]: {
    alertName: 'bitvavoExchangeLocked',
    substitutableNotifications: [
      NotificationType.PortfolioUpdateFinished,
      NotificationType.BuySellFinished,
    ],
    dismissesModals: portfolioUpdateRelatedModals,
    extraAction: updateExchangesAndPortfolioFn,
  },
};

function getSubTypesByAlertName(name: Nullish<AlertModalContentType>) {
  /*
   * Handle manual start of rebalance modal to be substitutable
   * by finished or error notification*/
  if (name === 'initialPortfolioRebalanceStart') {
    return [
      NotificationType.PortfolioUpdateFinished,
      NotificationType.PortfolioUpdateFailed,
    ];
  }

  // Used on depositing
  if (name === 'pendingExecRebalance') {
    return [
      NotificationType.PortfolioUpdateInProgress,
      NotificationType.PortfolioUpdateFinished,
      NotificationType.PortfolioUpdateFailed,
      NotificationType.UserExchangeLockedKraken,
      NotificationType.UserExchangeLockedBitvavo,
    ];
  }

  // Used on withdrawing
  if (name === 'pendingSellCrypto') {
    return [
      NotificationType.BuySellInProgress,
      NotificationType.BuySellFinished,
      NotificationType.BuySellFailed,
      NotificationType.UserExchangeLockedKraken,
      NotificationType.UserExchangeLockedBitvavo,
    ];
  }

  const type = findKey(
    notificationsData,
    ({ alertName }) => alertName === name
  ) as NotificationType | undefined;
  return type ? notificationsData[type].substitutableNotifications : [];
}

function updateExchangesAndPortfolioFn({
  client,
}: {
  client: ApolloClient<any>;
}) {
  return Promise.all([
    client.query({
      query: UserExchangesDocument,
      fetchPolicy: 'network-only',
    }),
    client.refetchQueries({
      include: ['dashboardData'],
    }),
  ]).catch((e) => {
    sendSentryError(e);
  });
}

const pollInterval = 3000;
